import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Card, CardContent, Stack, Button, Divider, List, ListItem, ListItemText, Chip, Grid, Alert, Paper, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { httpGet, httpPost } from '../lib/http';
import { useAuth } from '../context/AuthContext';
import { useStudyPlans } from '../context/StudyPlansContext';
import seed from '../data/studyPathsSeedDetailed';
import StudyNotes from '../components/StudyNotes';
import BookmarkButton from '../components/BookmarkButton';

export default function TopicView() {
  const { pathId, subjectId, topicId } = useParams();
  const [path, setPath] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const { getTrack } = useStudyPlans();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        // Prefer cached track from StudyPlansContext
        const cached = getTrack(pathId);
        if (cached && (Array.isArray(cached.orderedSubjects) || Array.isArray(cached.subjects))) {
          const ordered = Array.isArray(cached.orderedSubjects) ? cached.orderedSubjects : cached.subjects || [];
          setPath({ ...cached, orderedSubjects: ordered });
          setProgress(cached.progress || null);
          if (mounted) setLoading(false);
          return;
        }

        const res = await httpGet(`/api/study-paths/${encodeURIComponent(pathId)}`);
        if (res && res.ok) {
          const data = await res.json();
          if (!mounted) return;
          setPath(data);
        } else {
          const seedPath = seed.find(s => s.id === pathId);
          setPath(seedPath || null);
        }

        if (isAuthenticated && user) {
          try {
            const userId = user?.id || user?.userId || user?._id;
            const p = await httpGet(`/api/users/${encodeURIComponent(userId)}/path-progress/${encodeURIComponent(pathId)}`);
            if (p && p.ok) {
              const pdata = await p.json();
              setProgress(pdata);
            } else {
              setProgress(null);
            }
          } catch (e) {
            setProgress(null);
          }
        }
      } catch (e) {
        const seedPath = seed.find(s => s.id === pathId);
        setPath(seedPath || null);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [pathId, subjectId, topicId, isAuthenticated, user]);

  if (loading) return <Box sx={{ mt: 8, textAlign: 'center' }}>Loading…</Box>;
  if (!path) return <Container sx={{ mt: 8 }}><Typography>No study path found.</Typography></Container>;

  const subjects = Array.isArray(path.orderedSubjects) ? path.orderedSubjects : (Array.isArray(path.subjects) ? path.subjects : []);
  const subject = subjects.find(s => s.id === subjectId || s.title === subjectId);
  if (!subject) return <Container sx={{ mt: 8 }}><Typography>Subject not found.</Typography></Container>;

  const chaptersList = Array.isArray(subject?.chapters) ? subject.chapters : (Array.isArray(subject?.topics) ? [{ id: 'legacy', title: subject.title || 'Topics', topics: subject.topics }] : []);
  const topicsList = chaptersList.reduce((acc, ch) => acc.concat(Array.isArray(ch.topics) ? ch.topics : []), []);
  const topic = topicsList.find(t => (t.id && (t.id === topicId)) || t.title === topicId);
  if (!topic) return <Container sx={{ mt: 8 }}><Typography>Topic not found.</Typography></Container>;

  function topicCompleted() {
    if (!progress) return false;
    try {
      const tp = (progress.topicProgress || []).find(t => t.topicId === topicId);
      return Boolean(tp && tp.lessonCompleted);
    } catch {
      return false;
    }
  }

  async function markComplete() {
    if (!isAuthenticated || !user) {
      navigate(`/login?from=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    setMarking(true);
    try {
      const userId = user?.id || user?.userId || user?._id;
      const res = await httpPost(`/api/users/${encodeURIComponent(userId)}/path-progress/${encodeURIComponent(pathId)}/topic/${encodeURIComponent(topicId)}/complete`, {});
      if (res && res.ok) {
        const updated = await res.json();
        setProgress(updated);
      } else {
        const p = await httpGet(`/api/users/${encodeURIComponent(userId)}/path-progress/${encodeURIComponent(pathId)}`);
        if (p && p.ok) setProgress(await p.json());
      }
    } catch (e) {
      // ignore
    } finally {
      setMarking(false);
    }
  }

  function startPractice(practiceIndex) {
    // For MVP, open a simple practice modal or route to paper/question flow
    // We'll navigate to SubjectView for now or placeholder
    navigate(`/study-paths/${pathId}/subject/${subjectId}`);
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Enhanced Header */}
      <Paper sx={{ 
        p: 4, 
        mb: 4, 
        borderRadius: '16px', 
        background: 'radial-gradient(circle at 70% 30%, rgba(45, 91, 255, 0.05) 0%, rgba(255, 255, 255, 0) 50%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary'
      }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.8)', letterSpacing: 1 }}>
                {subject.title}
              </Typography>
              <Typography variant="h4" fontWeight={800} sx={{ color: 'white', mt: 0.5 }}>
                {topic.title}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Chip 
                  label={`${topic.estimatedMinutes || 0} Mins`}
                  sx={{ 
                    bgcolor: 'rgba(45, 91, 255, 0.08)', 
                    color: '#2D5BFF',
                    fontWeight: 700,
                    borderRadius: '16px',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem'
                  }}
                />
                <Chip 
                  label={`${topic.practiceCount || 0} Practice Items`}
                  sx={{ 
                    bgcolor: 'rgba(45, 91, 255, 0.08)', 
                    color: '#2D5BFF',
                    fontWeight: 700,
                    borderRadius: '16px',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem'
                  }}
                />
              </Stack>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={topicCompleted() ? "Completed! Great job!" : "Mark as complete when done"}>
                <Box>
                  <BookmarkButton
                    userId={user?.id || user?.userId || user?._id}
                    pathId={pathId}
                    subjectId={subjectId}
                    topicId={topicId}
                  />
                </Box>
              </Tooltip>
              {topicCompleted() ? (
                <Chip 
                  icon={<CheckCircleIcon sx={{ color: '#4CAF50 !important' }} />}
                  label="Mastered" 
                  sx={{ 
                    fontWeight: 800,
                    bgcolor: 'rgba(76, 175, 80, 0.08)',
                    color: '#2E7D32',
                    borderRadius: '16px',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem'
                  }}
                />
              ) : (
                <Chip 
                  label="In Progress" 
                  variant="outlined" 
                  sx={{ 
                    borderColor: 'rgba(45, 91, 255, 0.3)',
                    color: '#2D5BFF',
                    fontWeight: 700,
                    borderRadius: '16px',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem'
                  }}
                />
              )}
              <Button 
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(`/study-paths/${pathId}/subject/${subjectId}`)}
                sx={{ 
                  color: '#2D5BFF',
                  borderColor: 'rgba(45, 91, 255, 0.3)',
                  borderRadius: '16px',
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 3,
                  '&:hover': {
                    borderColor: '#2D5BFF',
                    bgcolor: 'rgba(45, 91, 255, 0.05)'
                  }
                }}
                variant="outlined"
              >
                Back
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {topicCompleted() && (
        <Alert 
          severity="success" 
          icon={<CheckCircleIcon />}
          sx={{ mb: 4, borderRadius: '16px' }}
        >
          <Typography variant="body2" fontWeight={600}>
            🎉 Excellent! You've completed this topic. Keep up the momentum!
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Lesson Content */}
            <Card sx={{ 
              borderRadius: '16px',
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Typography variant="h5" fontWeight={700}>
                    📚 Lesson Content
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ 
                  p: 4, 
                  bgcolor: 'action.hover',
                  borderRadius: '16px',
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography sx={{ 
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.8,
                    fontSize: '1.05rem'
                  }}>
                    {topic.content || '📖 Lesson content will be available soon. In the meantime, use the practice section to test your knowledge!'}
                  </Typography>
                </Box>
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    disabled={topicCompleted() || marking} 
                    onClick={markComplete}
                    startIcon={topicCompleted() ? <CheckCircleIcon /> : null}
                    sx={{
                      px: 5,
                      py: 1.5,
                      borderRadius: '16px',
                      fontSize: '1.05rem',
                      fontWeight: 800,
                      textTransform: 'none',
                      boxShadow: !topicCompleted() ? '0 8px 24px rgba(25,118,210,0.2)' : 'none',
                      '&:hover': {
                        boxShadow: !topicCompleted() ? '0 12px 32px rgba(25,118,210,0.3)' : 'none',
                      }
                    }}
                  >
                    {topicCompleted() ? '✅ Completed' : marking ? '⏳ Marking...' : '✓ Mark as Complete'}
                  </Button>
                  {!topicCompleted() && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Mark complete to track your progress
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Study Notes Section */}
            <StudyNotes
              pathId={pathId}
              subjectId={subjectId}
              topicId={topicId}
              userId={user?.id || user?.userId || user?._id}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            borderRadius: '16px',
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            top: 80
          }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                  💪 Practice Exercises
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              
                {!(topic.practiceCount) || topic.practiceCount === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  px: 2,
                  bgcolor: 'action.hover',
                  borderRadius: '16px'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    📝 Practice exercises coming soon! Focus on understanding the lesson content.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {Array.from({ length: topic.practiceCount || 0 }).map((_, i) => (
                    <ListItem 
                      key={i} 
                      sx={{ 
                        px: 0,
                        py: 1.5,
                        borderBottom: i < topic.practiceCount - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}
                    >
                      <ListItemText 
                        primary={
                          <Typography fontWeight={600}>
                            Practice Exercise {i + 1}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            ⏱️ Estimated 2 mins
                          </Typography>
                        }
                      />
                      <Button 
                        size="small" 
                        variant="outlined"
                        startIcon={<PlayCircleFilledIcon />}
                        onClick={() => startPractice(i)}
                        sx={{ 
                          borderRadius: '16px',
                          fontWeight: 700,
                          textTransform: 'none'
                        }}
                      >
                        Start
                      </Button>
                    </ListItem>
                  ))}
                </List>
              )}
              
              {topic.practiceCount > 0 && (
                <Box sx={{ 
                  mt: 3, 
                  p: 2.5, 
                  bgcolor: 'primary.lighter',
                  borderRadius: '16px',
                  border: '1px solid',
                  borderColor: 'primary.light'
                }}>
                  <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 600 }}>
                    💡 Tip: Complete all practice exercises to reinforce your learning!
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
